import ResultsList from '@theme/results-list';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

class HimalayanCollectionResults extends ResultsList {
  #events;
  #pageRequest;
  #operation = 0;
  #sampleObserver;
  #sampleKey;

  connectedCallback() {
    super.connectedCallback();
    this.#events?.abort();
    this.#events = new AbortController();
    this.#setBusy(false);
    this.#clearError();
    const options = { signal: this.#events.signal };
    window.addEventListener('popstate', this.#restoreHistory, options);
    this.addEventListener(StandardEvents.collectionUpdate, this.#filterChanged, options);
    this.addEventListener('click', this.#handleClick, options);
    this.#sampleKey = undefined;
    this.#sampleObserver?.disconnect();
    this.#sampleObserver = new MutationObserver(this.#syncSampleControls);
    this.#sampleObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-style-title'] });
    this.#syncSampleControls();
  }

  disconnectedCallback() {
    this.#operation += 1;
    this.#events?.abort();
    this.#pageRequest?.abort();
    this.#sampleObserver?.disconnect();
    super.disconnectedCallback();
  }

  #routeKey(url) {
    const value = new URL(url, location.href);
    return value.pathname + value.search;
  }

  #syncSampleControls = () => {
    if (this.querySelector('himalayan-reference-catalogue')) return;
    const grid = this.querySelector('[data-sample-grid]');
    const chips = this.querySelector('[data-style-chips]');
    if (!grid || !chips) return;
    const titles = Array.from(grid.children, (card) => card.getAttribute('data-style-title') || card.querySelector('.himalayan-catalogue-card__name')?.textContent?.trim() || '');
    const key = JSON.stringify(titles);
    if (key === this.#sampleKey) return;
    this.#sampleKey = key;
    const counts = new Map();
    for (const title of titles) counts.set(title, (counts.get(title) || 0) + 1);
    chips.querySelectorAll('[data-derived-chip]').forEach((chip) => chip.remove());
    for (const [title, count] of Array.from(counts).sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))) {
      const chip = document.createElement('span');
      chip.className = 'himalayan-facet-chip';
      chip.setAttribute('data-style-chip', '');
      chip.setAttribute('data-derived-chip', '');
      chip.append(document.createTextNode(title + ' '));
      const quantity = document.createElement('span');
      quantity.textContent = String(count);
      chip.append(quantity);
      chips.append(chip);
    }
    this.querySelectorAll('[data-style-total]').forEach((total) => { total.textContent = String(titles.length); });
  };

  #setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    const next = this.querySelector('[data-load-more]');
    if (next) next.setAttribute('aria-disabled', String(busy));
  }

  #clearError() {
    const error = this.querySelector('[data-collection-error]');
    if (error) error.hidden = true;
    const status = this.querySelector('[data-collection-status]');
    if (status) status.textContent = '';
  }

  #showError(url) {
    const error = this.querySelector('[data-collection-error]');
    if (!error) return;
    const link = error.querySelector('a');
    if (link) link.href = url;
    error.hidden = false;
  }

  #startOperation() {
    this.#pageRequest?.abort();
    this.#clearError();
    this.#setBusy(true);
    return ++this.#operation;
  }

  #filterChanged = (event) => {
    if (!this.contains(event.target) || !event.promise) return;
    const operation = this.#startOperation();
    const requestedURL = location.href;
    Promise.resolve(event.promise)
      .catch(() => {
        if (operation === this.#operation && this.isConnected) this.#showError(requestedURL);
      })
      .finally(() => {
        if (operation === this.#operation && this.isConnected) this.#setBusy(false);
      });
  };

  #restoreHistory = async () => {
    const requestedURL = new URL(location.href);
    const operation = this.#startOperation();
    try {
      await sectionRenderer.renderSection(this.sectionId, { url: new URL(requestedURL) });
    } catch {
      if (operation === this.#operation && this.isConnected) this.#showError(requestedURL.href);
    } finally {
      if (operation === this.#operation && this.isConnected) this.#setBusy(false);
    }
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const next = event.target.closest('[data-load-more]');
    if (!(next instanceof HTMLAnchorElement) || !this.contains(next)) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;
    this.#loadMore(next);
  };

  async #loadMore(next) {
    const url = new URL(next.href);
    const sourceRoute = this.#routeKey(location.href);
    const operation = this.#startOperation();
    this.#pageRequest = new AbortController();
    const signal = AbortSignal.any([this.#pageRequest.signal, AbortSignal.timeout(15000)]);

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(url), signal);
      if (operation !== this.#operation || !this.isConnected || sourceRoute !== this.#routeKey(location.href)) return;
      const response = new DOMParser().parseFromString(html, 'text/html');
      const incoming = response.querySelector('himalayan-collection-results');
      const currentGrid = this.querySelector('[ref="grid"]');
      const incomingGrid = incoming?.querySelector('[ref="grid"]');
      const currentPagination = this.querySelector('[data-collection-pagination]');
      const incomingPagination = incoming?.querySelector('[data-collection-pagination]');
      if (!currentGrid || !incomingGrid || !currentPagination || !incomingPagination) throw new Error('Incomplete collection page');

      const knownIds = new Set(Array.from(currentGrid.children, (card) => card.getAttribute('data-product-id')));
      const cards = Array.from(incomingGrid.children).filter((card) => card.hasAttribute('data-product-id') && !knownIds.has(card.getAttribute('data-product-id')));
      if (cards.length === 0) throw new Error('No new collection cards');
      currentGrid.append(...cards);
      const initialPrevious = currentPagination.querySelector('[data-previous-page]');
      const incomingPrevious = incomingPagination.querySelector('[data-previous-page]');
      if (initialPrevious && incomingPrevious) incomingPrevious.href = initialPrevious.href;
      else if (!initialPrevious) incomingPrevious?.remove();
      currentPagination.replaceWith(incomingPagination);
      this.pages.clear();
      history.pushState({}, '', url.href);
      const status = this.querySelector('[data-collection-status]');
      if (status) status.textContent = this.dataset.loadedMessage ?? '';
      cards[0].querySelector('a')?.focus({ preventScroll: true });
    } catch {
      if (operation === this.#operation && this.isConnected) this.#showError(url.href);
    } finally {
      if (operation === this.#operation && this.isConnected) this.#setBusy(false);
    }
  }
}

if (!customElements.get('himalayan-collection-results')) {
  customElements.define('himalayan-collection-results', HimalayanCollectionResults);
}
